const isKBZPayWebView = () => {
  return (
    typeof window !== "undefined" &&
    window.ma &&
    typeof window.ma.callNativeAPI === "function"
  );
};

const isAndroid = () => {
  if (typeof navigator === "undefined" || !navigator.userAgent) return false;
  return /android/i.test(navigator.userAgent);
};

export const chooseImage = (options = {}) => {
  return new Promise((resolve, reject) => {
    if (isKBZPayWebView() && isAndroid()) {
      const chooseImageFn = (window.ma && typeof window.ma.chooseImage === 'function') 
        ? window.ma.chooseImage 
        : null;

      if (chooseImageFn) {
        window.ma.chooseImage({
          count: options.count || 1,
          sizeType: options.sizeType || ["original"],
          sourceType: options.sourceType || ["album", "camera"],
          success: (res) => {
            const paths = res.tempFilePaths || (res.tempFilePath ? [res.tempFilePath] : []);
            if (paths.length > 0) {
              resolve({ tempFilePaths: paths });
            } else {
              reject(new Error("No image selected"));
            }
          },
          fail: (err) => reject(err)
        });
      } else {
        window.ma.callNativeAPI(
          "chooseImage",
          {
            count: options.count || 1,
            sizeType: options.sizeType || ["original"],
            sourceType: options.sourceType || ["album", "camera"],
          },
          (res) => {
            const paths = res.tempFilePaths || (res.tempFilePath ? [res.tempFilePath] : []);
            if (paths.length > 0) {
              resolve({ tempFilePaths: paths });
            } else {
              reject(new Error("No image selected"));
            }
          }
        );
      }
    } else {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = options.accept || "image/*";
      if (options.count && options.count > 1) {
        fileInput.multiple = true;
      }
      fileInput.style.display = "none";
      document.body.appendChild(fileInput);

      fileInput.onchange = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
          const tempFilePaths = [];
          for (let i = 0; i < files.length; i++) {
            tempFilePaths.push(URL.createObjectURL(files[i]));
          }
          document.body.removeChild(fileInput);
          resolve({
            tempFilePaths,
            files: Array.from(files),
          });
        } else {
          document.body.removeChild(fileInput);
          reject(new Error("No image selected"));
        }
      };

      fileInput.onerror = (err) => {
        document.body.removeChild(fileInput);
        reject(err);
      };

      const clickEvent = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: true,
      });
      fileInput.dispatchEvent(clickEvent);
    }
  });
};

export const getPlatformInfo = () => {
  if (typeof window === "undefined" || !window.ma) {
    return { isKBZPay: false, isAndroid: false, isIOS: false };
  }

  const ua = navigator.userAgent || "";
  const isAndroidDevice = /android/i.test(ua);

  return {
    isKBZPay: typeof window.ma.callNativeAPI === "function",
    isAndroid: isAndroidDevice,
    isIOS: /iPhone|iPad|iPod/i.test(ua),
  };
};

export const convertBlobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const convertTempFileToFile = (tempFilePath, fileName) => {
  return new Promise((resolve, reject) => {
    // Try fetch first
    fetch(tempFilePath)
      .then((response) => response.blob())
      .then((blob) => {
        resolve(new File([blob], fileName || `image_${Date.now()}.${blob.type.split('/')[1] || 'jpg'}`, {
          type: blob.type || "image/jpeg",
        }));
      })
      .catch((err) => {
        console.warn("Fetch failed for native path, trying XHR fallback:", tempFilePath);
        // Fallback to XHR
        const xhr = new XMLHttpRequest();
        xhr.open('GET', tempFilePath, true);
        xhr.responseType = 'blob';
        xhr.onload = () => {
          if (xhr.status === 200 || xhr.status === 0) {
            const blob = xhr.response;
            resolve(new File([blob], fileName || `image_${Date.now()}.${blob.type.split('/')[1] || 'jpg'}`, {
              type: blob.type || "image/jpeg"
            }));
          } else {
            reject(new Error(`XHR failed with status: ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error("XHR network error"));
        xhr.send();
      });
  });
};

const buildLog = () => {
  let log = "";

  return {
    add: (msg, data) => {
      const line =
        typeof data !== "undefined"
          ? `${msg} => ${JSON.stringify(data)}`
          : msg;

      console.log(line);
      log += line + "\n";
    },
    get: () => log,
  };
};

export const convertBase64ToFile = (base64String, fileName) => {
  return new Promise((resolve, reject) => {
    try {
      const arr = base64String.split(',');
      const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
      const bstr = atob(arr[arr.length - 1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      resolve(new File([u8arr], fileName || `image_${Date.now()}.jpg`, { type: mime }));
    } catch (err) {
      reject(err);
    }
  });
};

export const chooseImageNative = (options = {}) => {
  return new Promise((resolve, reject) => {
    if (isKBZPayWebView() && isAndroid()) {
      console.log("Calling ma.chooseImage API...");
      
      const handleResponse = async (res) => {
        // console.log("Native API Response:", JSON.stringify(res));
        
        // Try all possible property names for file paths
        const paths = 
          res.tempFilePaths || 
          res.apFilePaths || 
          res.filePaths || 
          (res.tempFilePath ? [res.tempFilePath] : []) ||
          (res.filePath ? [res.filePath] : []) ||
          (res.path ? [res.path] : []);
        
        // Check for direct base64 data
        const base64Data = res.base64 || res.data;

        if ((paths && paths.length > 0) || base64Data) {
          try {
            const files = [];
            const finalPaths = paths || [];

            // If we have base64, prioritize it as it's the most reliable data
            if (base64Data) {
              const b64 = base64Data.startsWith('data:') ? base64Data : `data:image/jpeg;base64,${base64Data}`;
              const file = await convertBase64ToFile(b64, `image_${Date.now()}.jpg`);
              files.push(file);
              // If no paths were provided, use the base64 string as the "path" for preview
              if (finalPaths.length === 0) finalPaths.push(b64);
            } else {
              // Convert paths to files
              for (let i = 0; i < finalPaths.length; i++) {
                const tempPath = finalPaths[i];
                const file = await convertTempFileToFile(tempPath, `image_${i}_${Date.now()}.jpg`);
                files.push(file);
              }
            }
            
            resolve({ tempFilePaths: finalPaths, files: files });
          } catch (err) {
            console.error("File conversion failed:", err);
            resolve({ tempFilePaths: paths || [], files: [] });
          }
        } else {
          const keys = Object.keys(res).join(", ");
          reject(new Error(`No image data found. Keys: ${keys}`));
        }
      };

      const chooseImageFn = (window.ma && typeof window.ma.chooseImage === 'function') 
        ? window.ma.chooseImage 
        : null;

      if (chooseImageFn) {
        window.ma.chooseImage({
          count: options.count || 1,
          sizeType: options.sizeType || ["original"],
          sourceType: options.sourceType || ["album", "camera"],
          success: handleResponse,
          fail: (err) => {
            console.error("ma.chooseImage Fail:", JSON.stringify(err));
            reject(err);
          }
        });
      } else {
        console.log("ma.chooseImage not found, falling back to callNativeAPI...");
        window.ma.callNativeAPI(
          "chooseImage",
          {
            count: options.count || 1,
            sizeType: options.sizeType || ["original"],
            sourceType: options.sourceType || ["album", "camera"],
          },
          handleResponse
        );
      }
    } else {
      reject(new Error("Native image picker not available"));
    }
  });
};